import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EditIcon from '@mui/icons-material/Edit';

import { PpmSuperactivityRow } from './procurement.models';

interface Props {
  rows: PpmSuperactivityRow[];
  canCreate: boolean;
  canManage: boolean;

  onCreate: () => void;
  onManage: (row: PpmSuperactivityRow) => void;
}

const money = (value: number) =>
  Number(value || 0).toLocaleString('fr-FR');

export default class PpmSuperactivitiesPanel extends React.Component<Props> {
  render() {
    const {
      rows,
      canCreate,
      canManage,
      onCreate,
      onManage,
    } = this.props;

    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="h6">
              Superactivities
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Group PPM activities into common procurement packages
              while preserving their original budget lines.
            </Typography>
          </Box>

          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreate}
            >
              Create Superactivity
            </Button>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {rows.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Stack
                alignItems="center"
                spacing={1}
                sx={{ py: 2 }}
              >
                <AccountTreeIcon color="disabled" />

                <Typography color="text.secondary">
                  No Superactivities have been created for this PPM.
                </Typography>

                {canCreate && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onCreate}
                  >
                    Create first Superactivity
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {rows.map((row) => (
              <Grid size = {{ xs:12, md:6, lg:4}} key={row.rid}>
                <Card
                  variant="outlined"
                  sx={{ height: '100%' }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box>
                          <Typography
                            variant="overline"
                            color="text.secondary"
                          >
                            {row.shc}
                          </Typography>

                          <Typography variant="subtitle1">
                            {row.nam}
                          </Typography>
                        </Box>

                        {row.psc && (
                          <Chip
                            size="small"
                            label={row.psc}
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      {row.dsc && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {row.dsc}
                        </Typography>
                      )}

                      <Divider />

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Activities
                          </Typography>

                          <Typography variant="h6">
                            {row.lct}
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Grouped Cost
                          </Typography>

                          <Typography variant="h6">
                            CFA {money(row.gam)}
                          </Typography>
                        </Box>
                      </Stack>

                      {(row.pmd || row.pnt) && (
                        <Stack direction="row" spacing={1}>
                          {row.pmd && (
                            <Chip
                              size="small"
                              label={`Method: ${row.pmd}`}
                            />
                          )}

                          {row.pnt && (
                            <Chip
                              size="small"
                              label={`Nature: ${row.pnt}`}
                            />
                          )}
                        </Stack>
                      )}

                      {canManage && (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => onManage(row)}
                        >
                          Manage Activities
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }
}